"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateMonthlyMortgagePayment = calculateMonthlyMortgagePayment;
exports.calculateGDSRatio = calculateGDSRatio;
exports.calculateTDSRatio = calculateTDSRatio;
exports.getStressTestRate = getStressTestRate;
exports.calculateAllRatios = calculateAllRatios;
exports.calculateIdealMortgageAmount = calculateIdealMortgageAmount;
exports.getStressTestStatus = getStressTestStatus;
exports.formatCurrencyDetailed = formatCurrencyDetailed;
function calculateMonthlyMortgagePayment(principal, annualRate, amortizationYears, compoundingFrequency = 'semi-annual') {
    if (principal <= 0 || annualRate <= 0 || amortizationYears <= 0)
        return 0;
    // Convert annual rate to decimal
    const annualRateDecimal = annualRate / 100;
    // Calculate effective monthly rate based on compounding frequency
    let monthlyRate;
    if (compoundingFrequency === 'monthly') {
        monthlyRate = annualRateDecimal / 12;
    }
    else if (compoundingFrequency === 'semi-annual') {
        // Canadian standard: semi-annual compounding
        // Convert semi-annual rate to effective monthly rate
        const semiAnnualRate = annualRateDecimal / 2;
        const effectiveAnnualRate = Math.pow(1 + semiAnnualRate, 2) - 1;
        monthlyRate = Math.pow(1 + effectiveAnnualRate, 1 / 12) - 1;
    }
    else { // annual
        monthlyRate = Math.pow(1 + annualRateDecimal, 1 / 12) - 1;
    }
    const numberOfPayments = amortizationYears * 12;
    if (monthlyRate === 0) {
        return principal / numberOfPayments;
    }
    const monthlyPayment = principal *
        (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
        (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    return monthlyPayment;
}
function calculateGDSRatio(monthlyMortgagePayment, propertyTaxMonthly, heatingCost, condoFees, monthlyIncome, condoFeeInclusionRate = 50) {
    // GDS = (Monthly Mortgage Payment + Property Tax + Heating + % of Condo Fees) / Monthly Income
    const monthlyHousingCosts = monthlyMortgagePayment + propertyTaxMonthly + heatingCost + (condoFees * (condoFeeInclusionRate / 100));
    return (monthlyHousingCosts / monthlyIncome) * 100;
}
function calculateTDSRatio(gdsAmount, monthlyDebtPayments, monthlyIncome) {
    // TDS = GDS Amount + Other Monthly Debt Payments / Monthly Income
    const totalMonthlyDebt = gdsAmount + monthlyDebtPayments;
    return (totalMonthlyDebt / monthlyIncome) * 100;
}
function getStressTestRate(contractRate) {
    // Canadian stress test: higher of contract rate + 2% or Bank of Canada 5-year benchmark (B-20 rate)
    const benchmarkRate = 5.20; // B-20 stress test rate
    return Math.max(contractRate + 2, benchmarkRate);
}
function calculateAllRatios(inputs, compoundingFrequency = 'semi-annual', benchmarkStressRate = 5.25, condoFeeInclusionRate = 50, rentalIncomePortionRate = 50, stressTestType = 'contract', rentalIncomeMethod = 'offset') {
    // Calculate total monthly income including rental income
    const baseMonthlyIncome = inputs.grossAnnualIncome / 12;
    let monthlyIncome = baseMonthlyIncome;
    // Apply rental income based on method
    if (inputs.isRentalIncomeEnabled && rentalIncomeMethod === 'add-back') {
        const rentalIncomeContribution = inputs.rentalIncomeMonthly * (rentalIncomePortionRate / 100);
        monthlyIncome = baseMonthlyIncome + rentalIncomeContribution;
    }
    const monthlyMortgagePayment = calculateMonthlyMortgagePayment(inputs.mortgageAmount, inputs.interestRate, inputs.amortizationYears, compoundingFrequency);
    // For display purposes, always show the base home expenses
    const monthlyHomeExpenses = inputs.propertyTaxMonthly + inputs.heatingCost + inputs.condoFees;
    // Calculate total expenses for GDS calculation
    let totalExpensesForGDS = inputs.propertyTaxMonthly + inputs.heatingCost + inputs.condoFees;
    // Deduct selected percentage of rental income from total expenses for GDS calculation
    if (inputs.isRentalIncomeEnabled && rentalIncomeMethod === 'offset') {
        const rentalOffset = inputs.rentalIncomeMonthly * (rentalIncomePortionRate / 100);
        totalExpensesForGDS = Math.max(0, totalExpensesForGDS - rentalOffset);
    }
    const adjustedHomeExpenses = totalExpensesForGDS;
    const totalMonthlyHousingCosts = monthlyMortgagePayment + monthlyHomeExpenses;
    // For GDS calculation, use base expenses and subtract rental income from total
    let gdsPropertyTax = inputs.propertyTaxMonthly;
    let gdsHeating = inputs.heatingCost;
    let gdsCondoFees = inputs.condoFees;
    // Apply rental income offset to GDS calculation using selected percentage
    if (inputs.isRentalIncomeEnabled && rentalIncomeMethod === 'offset') {
        const rentalOffset = inputs.rentalIncomeMonthly * (rentalIncomePortionRate / 100);
        const totalGDSExpenses = gdsPropertyTax + gdsHeating + (gdsCondoFees * (condoFeeInclusionRate / 100));
        const adjustedGDSExpenses = totalGDSExpenses - rentalOffset;
        // Proportionally reduce each component to maintain the ratio
        const reductionRatio = totalGDSExpenses > 0 ? adjustedGDSExpenses / totalGDSExpenses : 1;
        gdsPropertyTax = gdsPropertyTax * reductionRatio;
        gdsHeating = gdsHeating * reductionRatio;
        gdsCondoFees = gdsCondoFees * reductionRatio;
    }
    const gdsRatio = calculateGDSRatio(monthlyMortgagePayment, gdsPropertyTax, gdsHeating, gdsCondoFees, monthlyIncome, condoFeeInclusionRate);
    const gdsAmount = monthlyMortgagePayment + gdsPropertyTax + gdsHeating + (gdsCondoFees * (condoFeeInclusionRate / 100));
    const tdsRatio = calculateTDSRatio(gdsAmount, inputs.monthlyDebtPayments, monthlyIncome);
    // Stress test calculations based on type
    let stressTestRate;
    if (stressTestType === 'contract') {
        stressTestRate = inputs.interestRate; // Use contract rate as-is for CONTRACT
    }
    else { // b20
        stressTestRate = Math.max(inputs.interestRate + 2, benchmarkStressRate); // Higher of (rate + 2%) or 5.25%
    }
    const stressTestMortgagePayment = calculateMonthlyMortgagePayment(inputs.mortgageAmount, stressTestRate, inputs.amortizationYears, compoundingFrequency);
    const stressTestGDS = calculateGDSRatio(stressTestMortgagePayment, gdsPropertyTax, gdsHeating, gdsCondoFees, monthlyIncome, condoFeeInclusionRate);
    const stressTestGDSAmount = stressTestMortgagePayment + gdsPropertyTax + gdsHeating + (gdsCondoFees * (condoFeeInclusionRate / 100));
    const stressTestTDS = calculateTDSRatio(stressTestGDSAmount, inputs.monthlyDebtPayments, monthlyIncome);
    // Cash left calculation based on rental income method
    let cashLeftIncome = baseMonthlyIncome;
    let cashLeftExpenses = monthlyMortgagePayment + monthlyHomeExpenses + inputs.monthlyDebtPayments;
    // Both methods include rental income in cash left income calculation
    if (inputs.isRentalIncomeEnabled) {
        cashLeftIncome = baseMonthlyIncome + inputs.rentalIncomeMonthly;
    }
    const cashLeftGross = cashLeftIncome - cashLeftExpenses;
    return {
        monthlyMortgagePayment,
        monthlyHomeExpenses,
        monthlyIncome,
        gdsRatio,
        tdsRatio,
        stressTestRate,
        stressTestGDS,
        stressTestTDS,
        cashLeftGross,
        totalMonthlyHousingCosts
    };
}
function calculateIdealMortgageAmount(targetGdsRatio, inputs, compoundingFrequency = 'semi-annual', condoFeeInclusionRate = 50, rentalIncomePortionRate = 50, stressTestType = 'contract', rentalIncomeMethod = 'offset') {
    // Calculate monthly income
    const baseMonthlyIncome = inputs.grossAnnualIncome / 12;
    let monthlyIncome = baseMonthlyIncome;
    if (inputs.isRentalIncomeEnabled && rentalIncomeMethod === 'add-back') {
        const rentalIncomeContribution = inputs.rentalIncomeMonthly * (rentalIncomePortionRate / 100);
        monthlyIncome = baseMonthlyIncome + rentalIncomeContribution;
    }
    // Calculate GDS home expenses with rental income offset
    let gdsPropertyTax = inputs.propertyTaxMonthly;
    let gdsHeating = inputs.heatingCost;
    let gdsCondoFees = inputs.condoFees;
    if (inputs.isRentalIncomeEnabled && rentalIncomeMethod === 'offset') {
        const rentalOffset = inputs.rentalIncomeMonthly * (rentalIncomePortionRate / 100);
        const totalGDSExpenses = gdsPropertyTax + gdsHeating + (gdsCondoFees * (condoFeeInclusionRate / 100));
        const adjustedGDSExpenses = totalGDSExpenses - rentalOffset;
        const reductionRatio = totalGDSExpenses > 0 ? adjustedGDSExpenses / totalGDSExpenses : 1;
        gdsPropertyTax = gdsPropertyTax * reductionRatio;
        gdsHeating = gdsHeating * reductionRatio;
        gdsCondoFees = gdsCondoFees * reductionRatio;
    }
    // Calculate stress test rate
    let stressTestRate;
    if (stressTestType === 'contract') {
        stressTestRate = inputs.interestRate;
    }
    else {
        stressTestRate = Math.max(inputs.interestRate + 2, 5.25);
    }
    // Calculate target GDS amount based on target ratio
    const targetGdsAmount = monthlyIncome * (targetGdsRatio / 100);
    // Calculate home expenses for GDS calculation
    const homeExpensesForGds = gdsPropertyTax + gdsHeating + (gdsCondoFees * (condoFeeInclusionRate / 100));
    // Calculate maximum mortgage payment
    const maxMortgagePayment = targetGdsAmount - homeExpensesForGds;
    if (maxMortgagePayment <= 0)
        return 0;
    // Calculate mortgage amount from payment using stress test rate
    const numberOfPayments = inputs.amortizationYears * 12;
    // Calculate monthly rate based on compounding frequency
    let monthlyRate;
    const stressTestRateDecimal = stressTestRate / 100;
    if (compoundingFrequency === 'monthly') {
        monthlyRate = stressTestRateDecimal / 12;
    }
    else if (compoundingFrequency === 'semi-annual') {
        const semiAnnualRate = stressTestRateDecimal / 2;
        const effectiveAnnualRate = Math.pow(1 + semiAnnualRate, 2) - 1;
        monthlyRate = Math.pow(1 + effectiveAnnualRate, 1 / 12) - 1;
    }
    else {
        monthlyRate = Math.pow(1 + stressTestRateDecimal, 1 / 12) - 1;
    }
    if (monthlyRate === 0) {
        return maxMortgagePayment * numberOfPayments;
    }
    const mortgageAmount = maxMortgagePayment * ((1 - Math.pow(1 + monthlyRate, -numberOfPayments)) / monthlyRate);
    return Math.max(0, mortgageAmount);
}
function getStressTestStatus(gdsRatio, tdsRatio) {
    // Canadian mortgage qualification guidelines
    if (gdsRatio <= 32 && tdsRatio <= 40) {
        return {
            level: 'excellent',
            color: 'text-green-600',
            message: 'Excellent - Well within qualification guidelines'
        };
    }
    else if (gdsRatio <= 35 && tdsRatio <= 42) {
        return {
            level: 'good',
            color: 'text-yellow-600',
            message: 'Good - May qualify with strong credit and down payment'
        };
    }
    else if (gdsRatio <= 39 && tdsRatio <= 44) {
        return {
            level: 'caution',
            color: 'text-orange-600',
            message: 'Caution - May face challenges qualifying'
        };
    }
    else {
        return {
            level: 'poor',
            color: 'text-red-600',
            message: 'High risk - Unlikely to qualify without changes'
        };
    }
}
function formatCurrencyDetailed(amount) {
    return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
}
